import React from 'react';
import {
  GridActionsColDef,
  GridColDef,
  GridRenderCellParams,
} from '@mui/x-data-grid-pro';
import { Chip, IconButton, Stack, Tooltip } from '@mui/material';
import {CancelRounded, CheckCircleRounded, CheckRounded, EditRounded, FilterAltOutlined} from '@mui/icons-material';
import { BuildColumnsParams, TableRow } from './table.types';
import { STATUS_COLOR } from './table.helpers';

function renderRequiredHeader(label: string, required: boolean) {
  return (
    <Stack spacing={0} alignItems="center" sx={{ width: '100%', position: 'relative' }}>
      <div
        className="MuiDataGrid-columnHeaderTitle"
        style={{
          fontWeight: 'bold',
        }}
      >
        {label}
      </div>

      {required && (
        <Chip
          label="Kötelező"
          color="error"
          size="small"
          sx={{
            position: 'absolute',
            bottom: '4px',
            height: '18px',
            '& .MuiChip-label': {
              px: 0.75,
              fontSize: '10px',
              fontWeight: 700,
            },
          }}
        />
      )}
    </Stack>
  );
}

export function buildColumns({
                               editableFields,
                               hasMultipleProviders,
                               paginationModel,
                               setOpenHeaderFilters,
                               setOpenConfirmDialog,
                               onEditRow,
                               onFinalizeRow,
                               isFieldRequired,
                             }: BuildColumnsParams): (GridColDef<TableRow> | GridActionsColDef<TableRow>)[] {

  const columns: (GridColDef<TableRow> | GridActionsColDef<TableRow>)[] = [
    {
      field: 'row_number',
      headerName: '#',
      width: 50,
      sortable: false,
      filterable: false,
      editable: false,
      renderHeader: () => (
        <IconButton size="small" onClick={() => setOpenHeaderFilters((prev) => !prev)}>
          <FilterAltOutlined sx={{ color: '#fff' }} />
        </IconButton>
      ),
      renderCell: (params: GridRenderCellParams<TableRow>) => {
        const rowIndex =
          paginationModel.page * paginationModel.pageSize +
          params.api.getRowIndexRelativeToVisibleRows(params.id) +
          1;

        return <b>{rowIndex}</b>;
      },
    },
    {
      field: 'confirmation_status_name',
      headerName: 'Státusz',
      minWidth: 130,
      editable: false,
      sortable: false,
      renderHeader: () => renderRequiredHeader('Státusz', isFieldRequired('confirmation_status')),
      renderCell: (params: GridRenderCellParams<TableRow>) => {
        const statusId = (params.row as any).confirmation_status_id ?? 0;
        const bgColor = STATUS_COLOR[statusId] ?? '#666';
        const label = (params.row as any).confirmation_status_name ?? 'Pending';

        return (
          <Chip
            sx={{
              background: bgColor,
              color: '#fff',
            }}
            label={label}
          />
        );
      },
    },
    {
      field: 'type_name',
      headerName: 'Típus',
      minWidth: 100,
      editable: false,
      renderHeader: () => renderRequiredHeader('Típus', isFieldRequired('type')),
      renderCell: (params: GridRenderCellParams<TableRow>) => (
        <b style={{ color: (params.row as any).type_color || 'inherit' }}>
          {(params.row as any).type_name}
        </b>
      ),
    },
  ];

  if (hasMultipleProviders) {
    columns.push({
      field: 'service_provider_name',
      headerName: 'Szolgáltató',
      minWidth: 180,
      editable: false,
      renderHeader: () =>
        renderRequiredHeader('Szolgáltató', isFieldRequired('service_provider')),
    });
  }

  columns.push(
    {
      field: 'activity_name',
      headerName: 'Activity',
      minWidth: 160,
      editable: false,
      renderHeader: () => renderRequiredHeader('Activity', isFieldRequired('activity')),
    },
    {
      field: 'group_name',
      headerName: 'Csoport',
      minWidth: 160,
      editable: false,
      renderHeader: () => renderRequiredHeader('Csoport', isFieldRequired('group')),
    },
    {
      field: 'group_size',
      headerName: 'Létszám',
      width: 90,
      editable: false,
      renderHeader: () => renderRequiredHeader('Létszám', isFieldRequired('group_size')),
    },
    {
      field: 'date',
      headerName: 'Dátum',
      width: 110,
      editable: false,
      renderHeader: () => renderRequiredHeader('Dátum', isFieldRequired('date')),
    },
    {
      field: 'start_time',
      headerName: 'Kezdés',
      width: 90,
      editable: false,
      renderHeader: () => renderRequiredHeader('Kezdés', isFieldRequired('start_time')),
    },
    {
      field: 'address_to_name',
      headerName: 'Helyszín',
      minWidth: 220,
      flex: 1,
      editable: false,
      renderHeader: () => renderRequiredHeader('Helyszín', isFieldRequired('venue')),
    },{
      field: 'guide_names',
      headerName: 'Guide',
      minWidth: 220,
      flex: 1,
      editable: false,
      renderHeader: () => renderRequiredHeader('Guide', false),
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: '',
      width: 80,
      align: 'right',
      getActions: (params) => {
        const isRejected = params.row.type?.id === 4;
        const isRequest = params.row.type?.id === 3;

        return [
          <Tooltip key="edit" title="Szerkesztés">
            <IconButton disabled={isRejected} size="small" onClick={() => onEditRow(params.row)}>
              <EditRounded color={!isRejected? "primary": 'disabled'} fontSize="small" />
            </IconButton>
          </Tooltip>,
          <Tooltip key="reject" title="Foglalás lemondása">
            <IconButton size="small" disabled={isRejected} onClick={() => setOpenConfirmDialog(params.row)}>
              <CancelRounded color={!isRejected? "error": 'disabled'} fontSize="small" />
            </IconButton>
          </Tooltip>,
          <Tooltip key="finalization" title="Foglalás véglegesítése">
            <IconButton disabled={!isRequest} size="small" onClick={() => onFinalizeRow(params.row)}>
              <CheckCircleRounded color={isRequest? "success": 'disabled'} fontSize="small" />
            </IconButton>
          </Tooltip>
        ];;
      },
    } as GridActionsColDef<TableRow>
  );

  return columns.map((column) => ({
    ...column,
    editable: editableFields?.[column.field] ? true : column.editable ?? false,
  }));
}